import { useContext, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActivityInstance } from '@api';
import { InstanceContext } from '@/contexts/InstanceContext';
import BasicDialog, { BasicDialogContent } from '@/components/utils/dialogs/BasicDialog';
import { useChildTokens } from './useChildTokens';

/**
 * MissedActionsBar — actions-rad för en MISSAD aktivitet (skamfri statusmodell,
 * Pipeline 1). Visas ovanför timern när aktiviteten är missad och ger alltid
 * minst en väg framåt utan värderande/skam-ton:
 *  - "Jag gör det nu"   → startInstance()    (PUT /start)
 *  - "Markera klar"     → completeInstance()  (PUT /complete)
 *  - "Flytta till annan tid" → edit-flödet (/edit-activity/:id/single/Edit)
 *  - "Ta bort"          → deleteInstance()    (DELETE, med bekräftelse)
 *
 * Actionerna går mot InstanceContext (samma som resten av barnvyns
 * pågående-aktivitet). Färger via barntemat (inga hårdkodade hex). Teen ärver
 * komponenten via temat — ingen persona-gren här.
 */
type MissedActionsBarProps = {
  instance: ActivityInstance;
};

const removeDialogContent: BasicDialogContent = {
  title: 'child.missed.removeConfirmTitle',
  body: 'child.missed.removeConfirmBody',
  confirmText: 'child.missed.removeConfirm',
  cancelText: 'child.missed.removeCancel',
  confirmButtonColor: 'error',
};

const MissedActionsBar = ({ instance }: MissedActionsBarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tokens = useChildTokens();
  const { startInstance, completeInstance, deleteInstance } = useContext(InstanceContext);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Skydd: förutsätter ett laddat instance. Innan API:t hämtat instansen är
  // den tom ({}) — rendera då inget hellre än att skicka actions med
  // odefinierat id. (Anropsstället i ActivityInProgress är redan gated på
  // missad-status, men komponenten exporteras via barreln och kan återanvändas.)
  if (instance.activityInstanceId === undefined) {
    return null;
  }

  const handleMove = () => {
    sessionStorage.removeItem('formActivity');
    navigate(`/edit-activity/${instance.activityInstanceId}/single/Edit`);
  };

  const handleRemove = async () => {
    const removed = await deleteInstance();
    // Instansen finns inte längre → lämna pågående-vyn för barnets hem.
    if (removed) navigate('/');
  };

  return (
    <Box
      sx={{
        bgcolor: tokens.attentionLight,
        border: '1px solid',
        borderColor: tokens.attentionMain,
        borderRadius: '14px',
        p: '14px 16px',
      }}
    >
      <Typography
        sx={{ fontWeight: 800, fontSize: '0.95rem', color: tokens.attentionMain, mb: 1.5 }}
      >
        {t('child.missed.title')}
      </Typography>
      <Stack spacing={1}>
        <Box
          component="button"
          type="button"
          onClick={startInstance}
          sx={{
            width: '100%',
            minHeight: 48,
            p: '12px 16px',
            borderRadius: '12px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            border: 'none',
            fontWeight: 800,
            fontSize: '0.95rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:active': { transform: 'scale(0.99)' },
          }}
        >
          {t('child.missed.doNow')}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={completeInstance}
          sx={{
            width: '100%',
            minHeight: 48,
            p: '12px 16px',
            borderRadius: '12px',
            bgcolor: 'transparent',
            color: 'primary.main',
            border: '2px solid',
            borderColor: 'primary.main',
            fontWeight: 800,
            fontSize: '0.95rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:active': { transform: 'scale(0.99)' },
          }}
        >
          {t('child.missed.markDone')}
        </Box>
        <Stack direction="row" spacing={1}>
          <Box
            component="button"
            type="button"
            onClick={handleMove}
            sx={{
              flex: 1,
              minHeight: 48,
              p: '12px 16px',
              borderRadius: '12px',
              bgcolor: tokens.card,
              color: tokens.text,
              border: '1px solid',
              borderColor: tokens.border,
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:active': { transform: 'scale(0.99)' },
            }}
          >
            {t('child.missed.move')}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setConfirmOpen(true)}
            sx={{
              flex: 1,
              minHeight: 48,
              p: '12px 16px',
              borderRadius: '12px',
              bgcolor: tokens.card,
              color: tokens.textSoft,
              border: '1px solid',
              borderColor: tokens.border,
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:active': { transform: 'scale(0.99)' },
            }}
          >
            {t('child.missed.remove')}
          </Box>
        </Stack>
      </Stack>
      <BasicDialog
        open={confirmOpen}
        content={removeDialogContent}
        onConfirm={handleRemove}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
};

export default MissedActionsBar;
